import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ==================== TYPES ====================

export interface Note {
  id: string;
  title: string;
  content_markdown: string;
  status: 'draft' | 'archived';
  category: 'uncategorized' | 'job';
  created_at: string;
  updated_at: string;
}

export interface CreateNoteRequest {
  title: string;
  content_markdown: string;
  status?: 'draft' | 'archived';
  category?: 'uncategorized' | 'job';
}

export interface UpdateNoteRequest {
  title: string;
  content_markdown: string;
  status?: 'draft' | 'archived';
  category?: 'uncategorized' | 'job';
}

// ==================== QUERIES ====================

/**
 * Fetch all notes
 */
export const useNotes = () => {
  return useQuery({
    queryKey: ['notes'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes`, { headers });
      if (!res.ok) throw new Error('Failed to fetch notes');
      const data = await res.json();
      return data.data as Note[];
    },
  });
};

/**
 * Fetch a specific note by ID
 */
export const useNote = (id: string | undefined) => {
  return useQuery({
    queryKey: ['notes', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch note');
      const data = await res.json();
      return data.data as Note;
    },
    enabled: !!id,
  });
};

// ==================== MUTATIONS ====================

/**
 * Create a new note
 */
export const useCreateNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateNoteRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create note');
      }
      const response = await res.json();
      return response.data as Note;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
};

/**
 * Update an existing note
 */
export const useUpdateNote = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateNoteRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update note');
      }
      const response = await res.json();
      return response.data as Note;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      queryClient.invalidateQueries({ queryKey: ['notes', id] });
    },
  });
};

/**
 * Delete a single note
 */
export const useDeleteNote = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete note');
      }
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
};

/**
 * Delete multiple notes at once
 */
export const useBulkDeleteNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes/bulk-delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete notes');
      }
      const response = await res.json();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
};

/**
 * Archive multiple notes at once
 */
export const useBulkArchiveNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/notes/bulk-archive`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to archive notes');
      }
      const response = await res.json();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
};
